import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  MagnifyingGlass,
  Images,
  ArrowUpRight,
  Drop,
} from "@phosphor-icons/react";
import { PortalShell } from "@/components/PortalShell";
import { PortalLightbox } from "@/components/PortalLightbox";
import { portalApi, portalAsset, sinkSlug } from "@/lib/portalApi";
import { COL_CHOICES, useGridCols } from "@/lib/useGridCols";

const PAGE = 24;

const plainText = (html) =>
  (html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
const sinkTitle = (s) => plainText(s.description) || s.model || "Sink";

const Card = ({ it, onGallery, onOpen, backdrop }) => {
  const photos = it.photos || [];
  const stock = it.qty > 0 ? `${it.qty} in stock` : "Supplier Stock";
  const meta = [it.brand, it.material].filter(Boolean).join(" · ") || "Sink";
  const finishes = (it.finishes || []).slice(0, 5);
  return (
    <div
      data-testid={`portal-sink-item-${it.id}`}
      className="group flex flex-col overflow-hidden rounded-2xl bg-[#1a1a18] transition-transform duration-300 hover:-translate-y-1"
    >
      <div className={`relative aspect-square ${backdrop ? "s-cutout-stage" : "bg-slate-100"}`}>
        {it.photo ? (
          <button
            type="button"
            data-testid={`portal-sink-photo-${it.id}`}
            onClick={() => onGallery(it)}
            className="block h-full w-full cursor-zoom-in"
          >
            <img
              src={portalAsset(`/inventory/photo/${it.photo}?w=900`)}
              alt={it.model}
              className="h-full w-full object-contain p-4"
            />
          </button>
        ) : (
          <button
            type="button"
            onClick={() => onOpen(it)}
            className="flex h-full w-full items-center justify-center"
          >
            <Drop size={30} weight="thin" className="text-slate-300" />
          </button>
        )}
        {it.sink_type && (
          <span className="absolute left-3 top-3 rounded-md bg-[#c39e6d] px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-[#1a1a18] shadow-sm">
            {it.sink_type}
          </span>
        )}
        <span className="absolute bottom-3 right-3 z-[3] flex gap-1.5 opacity-0 transition-opacity duration-300 group-hover:opacity-100 focus-within:opacity-100">
          <button
            type="button"
            data-testid={`portal-sink-gallery-${it.id}`}
            disabled={!photos.length}
            onClick={() => onGallery(it)}
            title="Photos"
            className="flex h-10 w-10 items-center justify-center rounded-md bg-[#c39e6d] text-[#1a1a18] shadow-md ring-1 ring-black/10 transition-colors duration-200 hover:bg-[#b4895a] disabled:opacity-30"
          >
            <Images size={16} weight="bold" />
          </button>
        </span>
      </div>

      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-white/55">{meta}</div>
            <button
              type="button"
              data-testid={`portal-sink-open-name-${it.id}`}
              onClick={() => onOpen(it)}
              className="mt-1.5 block max-w-full truncate text-left text-lg font-bold tracking-tight text-white transition-colors duration-200 hover:text-[#c39e6d]"
            >
              {sinkTitle(it)}
            </button>
            <div className="mt-1.5 truncate text-[11px] text-white/45">
              {[it.bowls ? `${it.bowls} bowl` : "", it.size].filter(Boolean).join(" · ") || "Sink"}
            </div>
          </div>
          {it.price != null && (
            <div
              data-testid={`portal-sink-price-chip-${it.id}`}
              className="s-num shrink-0 whitespace-nowrap text-2xl leading-none text-white"
            >
              ${it.price.toFixed(2)}
            </div>
          )}
        </div>
        <div className="mt-5 flex items-end justify-between gap-3">
          <div className="flex min-w-0 items-center gap-1.5" data-testid={`portal-sink-finishes-${it.id}`}>
            {finishes.map((fin, n) =>
              fin.thumb ? (
                <img
                  key={n}
                  src={portalAsset(`/inventory/photo/${fin.thumb}?w=48`)}
                  alt=""
                  title={fin.name}
                  className="h-5 w-5 rounded-full object-cover ring-1 ring-white/25"
                />
              ) : (
                <span
                  key={n}
                  title={fin.name}
                  className="h-5 w-5 rounded-full bg-white/20 ring-1 ring-white/25"
                />
              ),
            )}
            {finishes.length === 0 && (
              <span className={`truncate text-[11px] ${it.requested ? "font-semibold text-emerald-400" : "text-white/45"}`}>
                {it.requested ? "Requested" : stock}
              </span>
            )}
          </div>
          <button
            type="button"
            data-testid={`portal-sink-open-${it.id}`}
            onClick={() => onOpen(it)}
            className="group/btn inline-flex h-11 shrink-0 items-center gap-2 px-1 text-[11px] font-semibold uppercase tracking-[0.15em] text-white transition-colors duration-200 hover:text-[#c39e6d]"
          >
            More Details
            <ArrowUpRight
              size={15}
              weight="bold"
              className="transition-transform duration-200 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5"
            />
          </button>
        </div>
      </div>
    </div>
  );
};

export default function PortalSinks() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [pricing, setPricing] = useState({ show: false, label: "" });
  const [catalog, setCatalog] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [type, setType] = useState("All");
  const [limit, setLimit] = useState(PAGE);
  const { cols, pick, choose, max } = useGridCols("portal-sinks-cols", 4);

  useEffect(() => {
    portalApi
      .get("/sinks")
      .then(({ data }) => {
        setItems(data.items || []);
        setPricing(data.pricing || { show: false, label: "" });
        setCatalog(data.catalog || {});
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const types = useMemo(() => {
    const s = new Set(items.map((i) => i.sink_type).filter(Boolean));
    return ["All", ...[...s].sort()];
  }, [items]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return items.filter((it) => {
      if (type !== "All" && it.sink_type !== type) return false;
      if (
        needle &&
        ![it.model, it.brand, it.material, it.sink_type, it.bowls, it.description]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(needle)
      )
        return false;
      return true;
    });
  }, [items, q, type]);

  useEffect(() => setLimit(PAGE), [q, type]);
  const visible = useMemo(() => shown.slice(0, limit), [shown, limit]);

  const [galleryId, setGalleryId] = useState(null);
  const [light, setLight] = useState(-1);
  const gallery = useMemo(() => {
    const it = items.find((x) => x.id === galleryId);
    if (!it) return [];
    return (it.photos || []).map((p, n) => ({
      path: p.path,
      thumb: p.thumb,
      title: it.model,
      sub: `${it.sink_type}${it.size ? ` · ${it.size}` : ""}${
        (it.photos || []).length > 1 ? ` · photo ${n + 1} of ${it.photos.length}` : ""
      }`,
    }));
  }, [items, galleryId]);
  const openGallery = (it) => {
    if (!(it.photos || []).length) return;
    setGalleryId(it.id);
    setLight(0);
  };

  return (
    <PortalShell>
      <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <h1 className="text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">
            Sinks
          </h1>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-slate-500 sm:text-base">
            Kitchen and vanity sinks we keep on the shelf — pair one with your slab and
            we&apos;ll cut it to fit.
          </p>
        </div>
        <span
          data-testid="portal-sinks-count"
          className="inline-flex shrink-0 items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-[#c39e6d]"
        >
          <span className="h-2.5 w-2.5 bg-[#c39e6d]" />
          {items.length} sinks in stock
        </span>
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <div className="relative min-w-[220px] flex-1">
          <MagnifyingGlass
            size={16}
            weight="bold"
            className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            data-testid="portal-sinks-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search a model, brand or material"
            className="h-12 w-full rounded-full border border-slate-200 bg-white pl-11 pr-4 text-sm text-slate-900 outline-none transition-colors duration-150 focus:border-[#c39e6d]"
          />
        </div>
        <div
          className="hidden items-center gap-2 lg:flex"
          data-testid="portal-sinks-cols-picker"
        >
          <span className="font-mono-data text-[10px] uppercase tracking-[0.22em] text-slate-500">
            View
          </span>
          {COL_CHOICES.map((n) => (
            <button
              key={n}
              type="button"
              data-testid={`portal-sinks-cols-${n}`}
              disabled={n > max}
              onClick={() => choose(n)}
              className={`h-9 w-9 rounded-lg text-xs font-bold transition-colors duration-150 disabled:opacity-25 ${
                pick === n
                  ? "bg-slate-900 text-white"
                  : "border border-slate-200 bg-white text-slate-500 hover:text-slate-900"
              }`}
            >
              {n}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2" data-testid="portal-sinks-types">
        {types.map((t) => (
          <button
            key={t}
            type="button"
            data-testid={`portal-sinks-type-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            onClick={() => setType(t)}
            className={`rounded-full px-4 py-2 text-[11px] font-bold uppercase tracking-widest transition-colors duration-150 ${
              type === t
                ? "bg-slate-900 text-white"
                : "border border-slate-200 bg-white text-slate-500 hover:text-slate-900"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {pricing.show && (
        <div
          className="mt-6 flex items-center gap-2 rounded-lg border border-[#c39e6d]/30 bg-[#c39e6d]/5 px-4 py-3"
          data-testid="portal-sinks-pricing-note"
        >
          <span className="font-mono-data text-[10px] uppercase tracking-[0.22em] text-[#c39e6d]">
            {pricing.label}
          </span>
          <span className="text-xs text-slate-500">
            Sink prices exclude cut-out and installation.
          </span>
        </div>
      )}

      {loading && (
        <div
          className="mt-8 grid gap-5"
          style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
        >
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="aspect-[3/4] animate-pulse rounded-xl border border-slate-200 bg-white"
            />
          ))}
        </div>
      )}

      {!loading && shown.length === 0 && (
        <div
          className="mt-8 flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-white/60 p-12 text-center"
          data-testid="portal-sinks-empty"
        >
          <Drop size={28} weight="thin" className="text-slate-400" />
          <h3 className="mt-4 text-base font-semibold text-slate-900">No sinks to show</h3>
          <p className="mt-2 max-w-sm text-sm text-slate-500">
            We don&apos;t have a sink matching that right now — ask us and we&apos;ll
            source one for your project.
          </p>
        </div>
      )}

      {!loading && shown.length > 0 && (
        <div
          className="mt-8 grid gap-5"
          data-testid="portal-sinks-grid"
          style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
        >
          {visible.map((it) => (
            <Card
              key={it.id}
              it={it}
              onGallery={openGallery}
              onOpen={(x) => navigate(`/portal/sinks/${sinkSlug(x.model, x.id)}`)}
              backdrop={!!catalog.cutout_backdrop && !!it.cutout}
            />
          ))}
        </div>
      )}

      {visible.length < shown.length && (
        <div className="mt-8 flex flex-col items-center gap-3">
          <button
            type="button"
            data-testid="portal-sinks-load-more"
            onClick={() => setLimit((n) => n + PAGE)}
            className="h-12 rounded-full border border-slate-200 bg-white px-10 text-[11px] font-bold uppercase tracking-widest text-slate-600 transition-colors duration-150 hover:border-[#c39e6d] hover:text-slate-900"
          >
            Load more
          </button>
        </div>
      )}

      {light >= 0 && gallery.length > 0 && (
        <PortalLightbox
          shots={gallery}
          index={light}
          srcOf={(path) => portalAsset(`/inventory/photo/${path}`)}
          onClose={() => setLight(-1)}
        />
      )}
    </PortalShell>
  );
}
